// combatpro/panels/monsters/MonsterDetailPanel.js
//
// Combat PRO - MonsterDetailPanel (Stage 40: Monster Panel Integration).
//
// Renders the ACTIVE monster's approved surface fields, bound to Monster Runtime
// state (state.monsters[state.currentCombatantIndex]). This is a faithful SUBSET
// of the legacy createMonsterPanel template (battletracker.html 5671-6519) - it
// is intentionally NOT a 1:1 reproduction of that 848-line panel. Only the
// surface fields approved for this stage are present:
//   - Custom Name        (input,  customName)        legacy 5761
//   - AC                 (input,  ac)                 legacy 5851
//   - Initiative         (select, initiative, 0-27)   legacy 5855 (manual edit)
//   - Roll Initiative    (button -> context handler)  legacy 5863
//   - DEX modifier box   (read-only display)          legacy 5862 / 8005-8011
//   - Total HP           (input,  maxHP)              legacy 5867
//   - Current HP         (input,  currentHP)          legacy 5871
//   - Remaining HP       (read-only display)          legacy 5875 / 7832
//   - HP bar             (width + text + condition)   legacy 5703 / 7835-7836
//   - Condition badge    (read-only display)          legacy 5712 / 7856-7857
//
// Legacy class names are reused verbatim so existing CSS styles this subset.
// Editable fields write through the single generic context handler
// updateMonsterField (Stage 40). The per-monster Roll button calls the existing
// context rollInitiative handler (Stage 39). HP percentage / condition is read
// from monsterEngine.hpPercentAndCondition; the DEX modifier from
// initiativeEngine.getAbilityModifier. No engine is modified.
//
// DEFERRED (NOT rendered here): the entire rest of the legacy panel - Monster
// Studio, identity/classification groups, ability scores, saves, resistances,
// immunities, abilities, weapons, spells, legendary, lair, morale, effects,
// notes, quick damage/heal, monster search. Those remain inert data and are
// owned by later stages. Only one panel (the active monster) is rendered; the
// legacy keep-all-16-panels DOM behaviour is deferred.
//
// No optional chaining. No em dash. Do not edit battletracker.html.

import React, { useContext, useState } from 'react';
import { CombatContext } from '../../contexts/CombatContext';
import { hpPercentAndCondition } from '../../monsters/monsterEngine';
import { getAbilityModifier } from '../../initiative/initiativeEngine';

// Manual initiative select option range, matching the legacy select (5856-5857):
// 0 shown as "--", then 1 through 27.
const INITIATIVE_OPTIONS = [];
for (let viOpt = 0; viOpt <= 27; viOpt++) { INITIATIVE_OPTIONS.push(viOpt); }

function MonsterDetailPanel() {
  const { state, rollInitiative, updateMonsterField, applyDamage, applyHealing } = useContext(CombatContext);
  // Stage 47 (Damage Runtime): local amount for the Quick Damage / Heal controls,
  // mirroring the legacy damageAmount-${index} input (value "0", reset to "0" after
  // each action). Declared before the early return to satisfy the rules of hooks.
  const [amount, setAmount] = useState('0');
  const index = state.currentCombatantIndex;
  const monster = state.monsters[index];
  if (!monster) { return null; }

  // HP percentage / condition tier (read-only render of updatePanel's data).
  const hp = hpPercentAndCondition(monster.currentHP, monster.maxHP);

  // DEX modifier display, matching updateInitiativeModifier (8005-8011).
  const dexMod = getAbilityModifier(monster.abilityScores.dex);
  const dexModStr = dexMod >= 0 ? '+' + dexMod : '' + dexMod;

  // Commit a numeric field, falling back to 0 on an empty/NaN input so the
  // controlled inputs never hold NaN (legacy stored raw parseInt, which could be
  // NaN on an empty field; this guard avoids the React controlled-NaN warning
  // and is the only intentional deviation).
  function commitNumber(field, raw) {
    const parsed = parseInt(raw, 10);
    updateMonsterField(index, field, isNaN(parsed) ? 0 : parsed);
  }

  return (
    <div className="monster-panel active" id={'panel-' + index}>
      <div className="form-group">
        <label>Custom Name</label>
        <input
          type="text"
          id={'customName-' + index}
          placeholder="Enter custom name"
          value={monster.customName}
          onChange={event => updateMonsterField(index, 'customName', event.target.value)}
        />
      </div>

      <div className="vi-rc-top">
        <div className="vi-rc-grid">
          <div className="vi-rc-card">
            <div className="vi-rc-label">AC</div>
            <input
              type="number"
              className="vi-rc-input"
              id={'ac-' + index}
              value={monster.ac}
              onChange={event => commitNumber('ac', event.target.value)}
            />
          </div>
          <div className="vi-rc-card">
            <div className="vi-rc-label">Initiative</div>
            <select
              className="vi-rc-input"
              id={'initiative-' + index}
              value={monster.initiative}
              onChange={event => updateMonsterField(index, 'initiative', parseInt(event.target.value, 10))}
            >
              {INITIATIVE_OPTIONS.map(value => (
                <option key={value} value={value}>{value === 0 ? '--' : value}</option>
              ))}
            </select>
          </div>
          <div className="vi-rc-card">
            <div className="vi-rc-label">Roll Initiative</div>
            <div className="vi-rc-mod-box" id={'init-mod-' + index}>{'(' + dexModStr + ')'}</div>
            <button className="vi-rc-btn vi-rc-btn-roll" onClick={() => rollInitiative(index)}>🎲 Roll</button>
          </div>
          <div className="vi-rc-card">
            <div className="vi-rc-label">Total HP</div>
            <input
              type="number"
              className="vi-rc-input"
              id={'maxHP-' + index}
              value={monster.maxHP}
              onChange={event => commitNumber('maxHP', event.target.value)}
            />
          </div>
          <div className="vi-rc-card">
            <div className="vi-rc-label">Current HP</div>
            <input
              type="number"
              className="vi-rc-input"
              id={'currentHP-' + index}
              value={monster.currentHP}
              onChange={event => commitNumber('currentHP', event.target.value)}
            />
          </div>
          <div className="vi-rc-card">
            <div className="vi-rc-label">Remaining HP</div>
            <div className="vi-rc-value" id={'remainingHp-' + index}>{hp.remaining}</div>
          </div>
        </div>
      </div>

      <div className="hp-bar-container">
        <div
          className={hp.barClass ? 'hp-bar ' + hp.barClass : 'hp-bar'}
          id={'hpBar-' + index}
          style={{ width: hp.percent + '%' }}
        >
          {hp.remaining + ' / ' + hp.maxHp + ' HP'}
        </div>
      </div>
      <div id={'conditionBadge-' + index}>
        {hp.maxHp > 0 ? <span className={'condition-badge ' + hp.conditionClass}>{hp.condition}</span> : null}
      </div>

      <div className="vi-box vi-damage-right">
        <div className="vi-quick-inner">
          <div className="vi-damage-hint">
            <div className="vi-damage-hint-title">⚡ Quick Damage / Heal</div>
            <div className="vi-damage-hint-body">Enter amount → click Deal Damage or Heal → HP updates instantly</div>
          </div>
          <div className="hp-controls">
            <input
              type="number"
              id={'damageAmount-' + index}
              placeholder="Amount"
              value={amount}
              onChange={event => setAmount(event.target.value)}
            />
            <button className="btn-danger" onClick={() => { applyDamage(index, parseInt(amount, 10)); setAmount('0'); }}>💥 Deal Damage</button>
            <button className="btn-success" onClick={() => { applyHealing(index, parseInt(amount, 10)); setAmount('0'); }}>💚 Heal</button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default MonsterDetailPanel;
